import { useState, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { CloseOutlined } from "@ant-design/icons"
import { Text } from "ui/Typography"
import { checkInGroup, checkOutGroup, resetGroup, resendGroupDocument } from "store/reservations"
import ReservationCard from "./ReservationCard"
import SendMessageButton from "../SendMessageButton"
import CheckInBadge from "../CheckIn/CheckInBadge/CheckInBadge"
import CheckInGuestsModal from "../CheckIn/CheckInGuestsModal/CheckInGuestsModal"
import IncompleteDocumentsModal from "../CheckIn/IncompleteDocumentsModal/IncompleteDocumentsModal"
import { deriveStateFromGuests } from "../CheckIn/checkInUtils"
import { useCheckInFlow } from "../CheckIn/useCheckInFlow"
import styles from "./index.module.css"

// Mobile (xs–sm) view for Reservations: a card per row. Owns its own check-in flow + row
// selection (the desktop table isn't mounted at this breakpoint), so the cards carry the same
// In/Out badge, selection checkbox, and Message Clients bar.
const ReservationMobileList = ({ items = [], onView }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const checkIn = useCheckInFlow({
    checkIn: checkInGroup,
    checkOut: checkOutGroup,
    reset: resetGroup,
    resend: resendGroupDocument
  })
  const { getCheckedInCount, getCheckedOutCount, getGuests } = checkIn

  // override-aware per-guest list so the badge + modal reflect optimistic check-in/out
  const data = useMemo(
    () => items.map((reservation) => {
      const guests = getGuests(reservation)
      return {
        ...reservation,
        key: reservation.id,
        checkedInCount: getCheckedInCount(reservation),
        checkedOutCount: getCheckedOutCount(reservation),
        guests,
        checkInState: deriveStateFromGuests(guests)
      }
    }),
    [items, getGuests, getCheckedInCount, getCheckedOutCount]
  )

  const selectedReservations = useMemo(
    () => data.filter((reservation) => selectedRowKeys.includes(reservation.key)),
    [data, selectedRowKeys]
  )

  // Drop selected keys that no longer exist after switching month/tab/page/filters, so the
  // selection bar doesn't linger with stale counts.
  useEffect(() => {
    setSelectedRowKeys((prev) => {
      const validKeys = new Set(data.map((reservation) => reservation.key))
      const pruned = prev.filter((key) => validKeys.has(key))
      return pruned.length === prev.length ? prev : pruned
    })
  }, [data])

  const toggleSelect = (key, isChecked) =>
    setSelectedRowKeys((prev) => (isChecked ? [...prev, key] : prev.filter((selected) => selected !== key)))

  const renderBadge = (record) => (
    <CheckInBadge
      state={record.checkInState}
      checkedInCount={record.checkedInCount}
      checkedOutCount={record.checkedOutCount}
      totalGuests={record.guestCount}
      onCheckIn={() => checkIn.openCheckIn(record, record.waiversComplete)}
      onCheckOut={() => checkIn.openCheckOut(record)}
      onReset={() => checkIn.reset(record)}
    />
  )

  return (
    <Flex vertical gap={10}>
      {selectedRowKeys.length > 0 && (
        <Flex align="center" gap={8} className={styles.selectionBar}>
          <Text variant="body" size="md" fw={500} color="var(--color-text-primary)">{selectedRowKeys.length} selected</Text>
          <SendMessageButton
            recipients={selectedReservations}
            label="Message Clients"
            buttonProps={{ type: "primary", size: "small", shape: "round" }}
            setSelectedRowKeys={setSelectedRowKeys}
          />
          <button
            type="button"
            className={styles.clearSelectionBtn}
            onClick={() => setSelectedRowKeys([])}
            aria-label="Clear selection"
          >
            <CloseOutlined />
          </button>
        </Flex>
      )}

      {data.map((item) => (
        <ReservationCard
          key={item.key}
          item={item}
          onView={onView}
          badge={renderBadge(item)}
          checked={selectedRowKeys.includes(item.key)}
          onToggleSelect={(isChecked) => toggleSelect(item.key, isChecked)}
        />
      ))}

      <IncompleteDocumentsModal
        open={!!checkIn.incompleteTarget}
        onClose={checkIn.closeIncomplete}
        onOverride={checkIn.handleOverride}
      />

      <CheckInGuestsModal
        open={!!checkIn.modalItem}
        onClose={checkIn.closeModal}
        bookingId={checkIn.modalItem?.bookingId}
        clientName={checkIn.modalItem?.name}
        paymentTag={checkIn.modalItem?.paymentTag}
        collectionTag={checkIn.modalItem?.collectionTag}
        guests={checkIn.getGuests(checkIn.modalItem)}
        isResending={checkIn.isResending}
        onToggleGuest={(guest) => checkIn.toggleGuest(checkIn.modalItem, guest)}
        onResend={(guest, document) => checkIn.resendDocument(checkIn.modalItem, guest, document)}
      />
    </Flex>
  )
}

ReservationMobileList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  onView: PropTypes.func
}

export default ReservationMobileList
